import React, { useState } from 'react';
import { PlanDay, PlanKind } from '@/lib/contentPlanner';
import {
  exportFeedCaptionsTxt,
  exportFeedSchedulerCsv,
  exportHashtagSetsTxt,
  exportStoryScriptTxt,
  exportStoryCsv,
  exportImageManifest,
  exportJsonBackup,
  exportPrintablePlan,
} from '@/lib/contentExport';
import {
  Download,
  FileText,
  FileSpreadsheet,
  Hash,
  Image as ImageIcon,
  Printer,
  Code2,
  Package,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from 'lucide-react';

interface Props {
  days: PlanDay[];
  kind: PlanKind;
}

interface Deliverable {
  title: string;
  description: string;
  format: string;
  icon: React.ReactNode;
  action: () => void;
  recommended?: boolean;
}

const DeliverablesPanel: React.FC<Props> = ({ days, kind }) => {
  const [expanded, setExpanded] = useState(true);
  const isFeed = kind === 'feed';

  const frameCount = days.reduce((sum, d) => sum + d.frames.length, 0);
  const approvedCount = days.reduce(
    (sum, d) => sum + d.frames.filter((f) => f.approval_status === 'Approved' || f.approval_status === 'Scheduled').length,
    0,
  );

  const feedDeliverables: Deliverable[] = [
    {
      title: 'Caption Sheet',
      description: 'Every caption + CTA + hashtags in one .txt file — copy-paste straight into Instagram.',
      format: '.txt',
      icon: <FileText className="w-5 h-5" />,
      action: () => exportFeedCaptionsTxt(days),
      recommended: true,
    },
    {
      title: 'Bulk Scheduler CSV',
      description: 'Upload to Later, Hootsuite, Buffer, or Meta Business Suite to bulk-schedule the entire week.',
      format: '.csv',
      icon: <FileSpreadsheet className="w-5 h-5" />,
      action: () => exportFeedSchedulerCsv(days),
      recommended: true,
    },
    {
      title: 'Hashtag Set Library',
      description: 'One hashtag block per post + a master alphabetical list. Save each block as an IG Saved Reply.',
      format: '.txt',
      icon: <Hash className="w-5 h-5" />,
      action: () => exportHashtagSetsTxt(days),
    },
    {
      title: 'Image Asset Manifest',
      description: 'A clickable gallery of every image — open it, then right-click → save each one, or use a bulk-download extension.',
      format: '.html',
      icon: <ImageIcon className="w-5 h-5" />,
      action: () => exportImageManifest(days, kind),
    },
    {
      title: 'Printable Weekly Plan',
      description: 'Print-ready PDF view of the full week — perfect for client review meetings.',
      format: '.html → .pdf',
      icon: <Printer className="w-5 h-5" />,
      action: () => exportPrintablePlan(days, kind),
    },
    {
      title: 'JSON Backup',
      description: 'Raw structured data for developers, archives, or migration.',
      format: '.json',
      icon: <Code2 className="w-5 h-5" />,
      action: () => exportJsonBackup(days, kind),
    },
  ];

  const storyDeliverables: Deliverable[] = [
    {
      title: 'Shooting Script',
      description: 'The full producer-ready script — every frame, every sticker, every overlay text, in shooting order.',
      format: '.txt',
      icon: <FileText className="w-5 h-5" />,
      action: () => exportStoryScriptTxt(days),
      recommended: true,
    },
    {
      title: 'Story Schedule CSV',
      description: 'One row per frame — hand to your designer or load into a story-planning tool.',
      format: '.csv',
      icon: <FileSpreadsheet className="w-5 h-5" />,
      action: () => exportStoryCsv(days),
      recommended: true,
    },
    {
      title: 'Image Asset Manifest',
      description: 'Every background image at 9:16 ratio, ready for download into Canva, Figma, or the Stories editor.',
      format: '.html',
      icon: <ImageIcon className="w-5 h-5" />,
      action: () => exportImageManifest(days, kind),
    },
    {
      title: 'Printable Story Plan',
      description: 'A beautifully laid-out PDF preview — every story frame with its overlay, status, and link destination.',
      format: '.html → .pdf',
      icon: <Printer className="w-5 h-5" />,
      action: () => exportPrintablePlan(days, kind),
    },
    {
      title: 'JSON Backup',
      description: 'Raw structured data export.',
      format: '.json',
      icon: <Code2 className="w-5 h-5" />,
      action: () => exportJsonBackup(days, kind),
    },
  ];

  const deliverables = isFeed ? feedDeliverables : storyDeliverables;

  const downloadAll = () => {
    deliverables.forEach((d, i) => setTimeout(d.action, i * 400));
  };

  if (days.length === 0) return null;

  return (
    <div className="bg-white border border-[#C9A961]/30 mb-6">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 hover:bg-[#C9A961]/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#C9A961]/15 border border-[#C9A961]/40 flex items-center justify-center">
            <Package className="w-5 h-5 text-[#8B6F47]" />
          </div>
          <div className="text-left">
            <h3 className="font-serif text-lg text-[#0D1B2A] font-light flex items-center gap-2">
              Deliverables
              <span className="font-sans text-[10px] tracking-[0.2em] uppercase bg-[#C9A961] text-white px-2 py-0.5">
                Ready to Post
              </span>
            </h3>
            <p className="font-sans text-xs text-slate-500 mt-0.5">
              {frameCount} {isFeed ? 'posts' : 'frames'} · {approvedCount} approved & ready to ship · Download what your team actually needs
            </p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-100 p-5 bg-slate-50/40">
          {/* Quick action bar */}
          <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-200">
            <div className="font-sans text-[11px] tracking-[0.2em] uppercase text-slate-500">
              {isFeed ? 'Feed Post Exports' : 'Story Exports'}
            </div>
            <button
              onClick={downloadAll}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0D1B2A] text-white font-sans text-[10px] tracking-[0.2em] uppercase hover:bg-[#C9A961] transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Download Everything
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {deliverables.map((d) => (
              <button
                key={d.title}
                onClick={d.action}
                className="group relative text-left p-4 bg-white border border-slate-200 hover:border-[#C9A961] hover:shadow-md transition-all"
              >
                {d.recommended && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 font-sans text-[8px] tracking-wider uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5">
                    <Sparkles className="w-2.5 h-2.5" /> Pick this
                  </span>
                )}
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-9 h-9 bg-slate-100 border border-slate-200 flex items-center justify-center text-[#1A3B5C] group-hover:bg-[#C9A961]/15 group-hover:text-[#8B6F47] transition-colors">
                    {d.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-sm text-[#0D1B2A] mb-0.5 flex items-center gap-2">
                      {d.title}
                      <span className="font-sans text-[9px] tracking-wider uppercase text-slate-400 bg-slate-50 px-1.5 py-0.5 border border-slate-200">
                        {d.format}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="font-sans text-xs text-slate-600 leading-relaxed">{d.description}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 font-sans text-[10px] tracking-wider uppercase text-[#1A3B5C] group-hover:text-[#C9A961]">
                  <Download className="w-3 h-3" /> Download
                </div>
              </button>
            ))}
          </div>

          {/* How-to footer */}
          <div className="mt-5 p-4 bg-[#0D1B2A] text-white/80">
            <div className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961] mb-2">
              How your social manager uses these
            </div>
            <ol className="font-sans text-xs space-y-1.5 leading-relaxed list-decimal list-inside text-white/70">
              {isFeed ? (
                <>
                  <li>Download the <strong className="text-white">Bulk Scheduler CSV</strong> and import it into Later / Hootsuite / Buffer to schedule the whole week in one go.</li>
                  <li>Download the <strong className="text-white">Image Asset Manifest</strong>, open it, and right-click each image to save — or use a bulk-download browser extension.</li>
                  <li>For manual posting, the <strong className="text-white">Caption Sheet</strong> has a ready-to-paste block for each post (caption + CTA + hashtags already formatted).</li>
                  <li>Send the <strong className="text-white">Printable Weekly Plan</strong> to the client for sign-off before publishing.</li>
                </>
              ) : (
                <>
                  <li>Send the <strong className="text-white">Shooting Script</strong> to your content producer — every frame, every overlay text, every sticker is spelled out.</li>
                  <li>Hand the <strong className="text-white">Image Asset Manifest</strong> to your designer to build the 9:16 graphics in Canva or Figma.</li>
                  <li>Use the <strong className="text-white">Story Schedule CSV</strong> in a story-planning tool (or as a daily checklist for the publisher).</li>
                  <li>Print the <strong className="text-white">Story Plan PDF</strong> for the client review call.</li>
                </>
              )}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliverablesPanel;
